import { useCallback, useEffect, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { Button } from '../../../components/Button';
import { approvalsService } from '../../../services/approvals/approvals.service';
import layoutClasses from '../pos-shell-page.module.scss';

// Summary: Pending-manager-approval state, SSE event handling, and the blocking toast (Rule 32/45 extraction from PosShellPage.tsx).
export function usePendingApprovals({
  tenantId,
  employeeSession,
  isManager,
  currentEmployeeId,
  activeTab,
  setActiveTab,
  selectedOrderIdRef,
}: {
  tenantId: string;
  employeeSession: string | null;
  isManager: boolean;
  currentEmployeeId: number | null;
  activeTab: string;
  setActiveTab: (tab: 'areas' | 'orders' | 'limit-items' | 'table-history') => void;
  // Id of the order this device is viewing; gates the cancel-order eject to it only.
  selectedOrderIdRef: import('react').MutableRefObject<number | null>;
}) {
  const [pendingApprovalIds, setPendingApprovalIds] = useState<Set<number>>(() => new Set());
  const [pendingApprovalOrderIds, setPendingApprovalOrderIds] = useState<Set<number>>(() => new Set());
  // orderId -> pending request id, so the blocking toast's cancel button can target the right request.
  const [pendingApprovalIdByOrderId, setPendingApprovalIdByOrderId] = useState<Map<number, number>>(() => new Map());
  const approvalEventCallbackRef = useRef<((request: import('shared-models/browser').ApprovalRequestView) => void) | null>(null);
  const cancelAllSentLinesCallbackRef = useRef<(() => void) | null>(null);
  const handleApprovalEvent = useCallback((event: {
    type: 'approval_request.created' | 'approval_request.decided';
    request: import('shared-models/browser').ApprovalRequestView;
    autoApprovedEmployeeGeneralItem?: boolean;
  }) => {
    approvalEventCallbackRef.current?.(event.request);
    setPendingApprovalIds((previous) => {
      const next = new Set(previous);
      if (event.request.status === 'pending') {
        next.add(event.request.id);
      } else {
        next.delete(event.request.id);
      }
      return next;
    });
    setPendingApprovalOrderIds((previous) => {
      const next = new Set(previous);
      if (event.request.status === 'pending') {
        next.add(event.request.orderId);
      } else {
        next.delete(event.request.orderId);
      }
      return next;
    });
    setPendingApprovalIdByOrderId((previous) => {
      const next = new Map(previous);
      if (event.request.status === 'pending') {
        next.set(event.request.orderId, event.request.id);
      } else {
        next.delete(event.request.orderId);
      }
      return next;
    });
    // Auto-dismiss the "בקשת מנהל פעילה" blocking toast for this order once its request is decided.
    if (event.type === 'approval_request.decided') {
      notifications.hide(`pending-approval-${event.request.orderId}`);
      notifications.hide(`pending-approval-${event.request.id}`);
      notifications.hide('pending-approval');
    }
    // Eject to areas only the device viewing the cancelled order; notified bystanders stay put.
    if (
      activeTab === 'orders'
      && event.type === 'approval_request.decided'
      && event.request.status === 'approved'
      && event.request.payload.actionType === 'cancel'
      && event.request.payload.cancelScope === 'order'
      && selectedOrderIdRef.current === event.request.orderId
    ) {
      setActiveTab('areas');
    }
    if (isManager && event.type === 'approval_request.created' && event.request.status === 'pending') {
      const newCount = new Set([...pendingApprovalIds, event.request.id]).size;
      const toastId = `new-approval-request-${event.request.id}`;
      notifications.show({
        id: toastId,
        color: 'orange',
        title: 'בקשת אישור חדשה',
        autoClose: 3000,
        message: (
          <div
            role="button"
            tabIndex={0}
            data-testid={`pending-approval-toast-${event.request.id}`}
            className={layoutClasses.newApprovalRequestToast}
            onClick={() => {
              setActiveTab('orders');
              notifications.hide(toastId);
            }}
            onKeyDown={(keyEvent) => {
              if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
                setActiveTab('orders');
                notifications.hide(toastId);
              }
            }}
          >
            {`${newCount} בקשות ממתינות לאישור מנהל`}
          </div>
        ),
      });
    }
    // A requester self-cancel still reaches every device to clear pending state, but is not a
    // manager decision and must not produce a rejection toast for managers or other users.
    const isRequesterSelfCancellation = event.request.status === 'denied'
      && event.request.decidedByPk === event.request.requestedByPk;
    // Decided-toast: all managers + the requesting employee, but never the actor about his own action.
    if (!event.autoApprovedEmployeeGeneralItem
      && !isRequesterSelfCancellation
      && (isManager || event.request.requestedByPk === currentEmployeeId)
      && event.request.decidedByPk !== currentEmployeeId
      && event.type === 'approval_request.decided') {
      const wasSelfCancelled = selfCancelledRequestIdsRef.current.delete(event.request.id);
      if (!wasSelfCancelled) {
        const approved = event.request.status === 'approved';
        const tableInfo = event.request.tableLabel ? ` לשולחן ${event.request.tableLabel}` : '';
        notifications.show({
          color: approved ? 'green' : 'red',
          title: approved ? 'הבקשה אושרה' : 'הבקשה נדחתה',
          message: `${approved ? 'המנהל אישר את הבקשה' : 'המנהל דחה את הבקשה'}${tableInfo}`,
          autoClose: 3000,
        });
      }
    }
  }, [activeTab, isManager, currentEmployeeId, pendingApprovalIds, setActiveTab, selectedOrderIdRef]);

  // Replaces the whole pending state from a source-of-truth list (mount + tab-entry reconciliation).
  const reconcilePendingRequests = useCallback((requests: import('shared-models/browser').ApprovalRequestView[]) => {
    setPendingApprovalIds(new Set(requests.map((request) => request.id)));
    setPendingApprovalOrderIds(new Set(requests.map((request) => request.orderId)));
    setPendingApprovalIdByOrderId(new Map(requests.map((request) => [request.orderId, request.id])));
  }, []);

  // Set when the mount effect just fetched, so the orders self-heal skips its coincident first run.
  const mountReconcileJustRanRef = useRef(false);
  useEffect(() => {
    if (!isManager || !employeeSession) {
      setPendingApprovalIds(new Set());
      setPendingApprovalOrderIds(new Set());
      setPendingApprovalIdByOrderId(new Map());
      return;
    }

    let cancelled = false;
    mountReconcileJustRanRef.current = true;
    approvalsService.listAllPending(tenantId, employeeSession)
      .then((requests) => {
        if (cancelled) return;
        reconcilePendingRequests(requests);
      })
      .catch(() => {
        if (cancelled) return;
        reconcilePendingRequests([]);
      });

    return () => {
      cancelled = true;
    };
  }, [tenantId, employeeSession, isManager, reconcilePendingRequests]);

  // Self-heal: re-sync glow vs source of truth on orders entry (clears stale glow from missed delta).
  useEffect(() => {
    const skipCoincidentMountFetch = mountReconcileJustRanRef.current;
    mountReconcileJustRanRef.current = false;
    if (activeTab !== 'orders' || !isManager || !employeeSession) return;
    if (skipCoincidentMountFetch) return;

    let cancelled = false;
    approvalsService.listAllPending(tenantId, employeeSession)
      .then((requests) => {
        if (cancelled) return;
        reconcilePendingRequests(requests);
      })
      .catch(() => {
        // Leave the current glow as-is; the next tab entry retries the reconciliation.
      });

    return () => {
      cancelled = true;
    };
  }, [activeTab, tenantId, employeeSession, isManager, reconcilePendingRequests]);

  const cancelPendingApprovalInFlightRef = useRef(false);
  // Request ids the requester just self-cancelled via the "בטל בקשה" toast button — suppresses
  // the "המנהל דחה את הבקשה" toast for the SSE decided-event that follows our own cancel.
  const selfCancelledRequestIdsRef = useRef<Set<number>>(new Set());
  // Currently-shown pending-approval toast ids — hidden on tab navigation (same as pressing X).
  const openPendingApprovalToastIdsRef = useRef<Set<string>>(new Set());

  const showPendingApprovalBlockingToast = useCallback((orderId: number) => {
    const requestId = pendingApprovalIdByOrderId.get(orderId) ?? null;
    const toastId = `pending-approval-${orderId}`;
    openPendingApprovalToastIdsRef.current.add(toastId);

    const handleCancelRequest = async () => {
      if (requestId == null || !employeeSession || cancelPendingApprovalInFlightRef.current) {
        return;
      }
      cancelPendingApprovalInFlightRef.current = true;
      selfCancelledRequestIdsRef.current.add(requestId);
      try {
        await approvalsService.decide(tenantId, requestId, 'denied', employeeSession);
        notifications.hide(toastId);
        openPendingApprovalToastIdsRef.current.delete(toastId);
      } catch (err: unknown) {
        selfCancelledRequestIdsRef.current.delete(requestId);
        notifications.show({
          color: 'red',
          title: 'שגיאה בביטול הבקשה',
          message: err instanceof Error ? err.message : 'הביטול נכשל',
          autoClose: 3000,
        });
      } finally {
        cancelPendingApprovalInFlightRef.current = false;
      }
    };

    notifications.show({
      id: toastId,
      color: 'orange',
      title: 'בקשת מנהל פעילה',
      autoClose: false,
      classNames: { root: layoutClasses.pendingApprovalToastRoot },
      onClose: () => {
        openPendingApprovalToastIdsRef.current.delete(toastId);
      },
      message: (
        <div className={layoutClasses.pendingApprovalToast}>
          <span>קיימת בקשת מנהל פתוחה להזמנה, יש להמתין לאישור או דחייה</span>
          {requestId != null ? (
            <Button variant="cancel" size="sm" onClick={handleCancelRequest} dataTestId="pending-approval-cancel-request">
              בטל בקשה
            </Button>
          ) : null}
        </div>
      ),
    });
  }, [employeeSession, pendingApprovalIdByOrderId, tenantId]);

  // Leaving the tab dismisses any open pending-approval toast, same as pressing its own X —
  // it should not follow the user to a different screen.
  useEffect(() => {
    openPendingApprovalToastIdsRef.current.forEach((toastId) => notifications.hide(toastId));
    openPendingApprovalToastIdsRef.current.clear();
  }, [activeTab]);

  const ensureOrderHasPendingApproval = useCallback(async (orderId: number | null | undefined): Promise<boolean> => {
    if (orderId == null || !employeeSession) {
      return false;
    }
    if (pendingApprovalOrderIds.has(orderId)) {
      return true;
    }

    try {
      const requests = await approvalsService.listPending(tenantId, orderId, employeeSession);
      if (requests.length === 0) {
        return false;
      }
      setPendingApprovalIds((previous) => {
        const next = new Set(previous);
        requests.forEach((request) => next.add(request.id));
        return next;
      });
      setPendingApprovalOrderIds((previous) => {
        const next = new Set(previous);
        requests.forEach((request) => next.add(request.orderId));
        return next;
      });
      setPendingApprovalIdByOrderId((previous) => {
        const next = new Map(previous);
        requests.forEach((request) => next.set(request.orderId, request.id));
        return next;
      });
      return true;
    } catch {
      return false;
    }
  }, [employeeSession, pendingApprovalOrderIds, tenantId]);

  return {
    pendingApprovalIds,
    setPendingApprovalIds,
    pendingApprovalOrderIds,
    setPendingApprovalOrderIds,
    approvalEventCallbackRef,
    cancelAllSentLinesCallbackRef,
    handleApprovalEvent,
    showPendingApprovalBlockingToast,
    ensureOrderHasPendingApproval,
  };
}
